import React, { createElement } from 'react';
// Two logo concept variations based on the green tile reference, both on
// pure white backgrounds. Brand greens used: #006241 (deep), #17A347
// (mid), #04A05C (accent). Renders the lockups at multiple sizes for
// review (large hero, medium, small/favicon-style).
//
// Preview at /logo-concepts.
const GREEN_DEEP = '#006241';
const GREEN_MID = '#17A347';
/**
 * Variation A — Edge-to-edge "253" with "GRAVEL" wordmark below.
 * Pure typographic lockup, no container. 253 fills the available width.
 */
function LogoVariantA({
  size = 320
}: {
  size?: number;
}) {
  // Letter-spacing on GRAVEL is tuned so its visual width matches 253
  // exactly at this aspect ratio.
  return <div style={{
    width: size
  }} className="flex flex-col items-stretch select-none" aria-label="253 Gravel logo, variant A" data-id="element-1173" data-mp="3dhaig0jqf1a6">
      <svg viewBox="0 0 300 200" className="w-full block" preserveAspectRatio="xMidYMid meet" data-id="element-1174" data-mp="1o35nzg0sk7q6">
        <text x="150" y="170" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight={900} fontSize={230} letterSpacing="-10" fill={GREEN_DEEP} data-id="element-1175" data-mp="1j53ue15qoq0g">
          253
        </text>
      </svg>
      <svg viewBox="0 0 300 50" className="w-full block -mt-1" preserveAspectRatio="xMidYMid meet" data-id="element-1176" data-mp="2bhrpb3836dyl">
        <text x="150" y="38" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight={800} fontSize={48} letterSpacing="6" fill={GREEN_DEEP} data-id="element-1177" data-mp="2fhjy9h1e6m4h">
          GRAVEL
        </text>
      </svg>
    </div>;
}
/**
 * Variation B — "253" edge-to-edge in green; "GRAVEL" reversed out of a
 * solid green bar directly beneath. Mirrors the reference tile's structure
 * (number block + label block) but on white with green replacing the
 * background fill.
 */
function LogoVariantB({
  size = 320
}: {
  size?: number;
}) {
  return <div style={{
    width: size
  }} className="flex flex-col items-stretch select-none" aria-label="253 Gravel logo, variant B" data-id="element-1178" data-mp="14st4lg7yp1o9">
      <svg viewBox="0 0 300 200" className="w-full block" preserveAspectRatio="xMidYMid meet" data-id="element-1179" data-mp="18tmu2e11yoj9">
        <text x="150" y="170" textAnchor="middle" fontFamily="'Inter', system-ui, sans-serif" fontWeight={900} fontSize={230} letterSpacing="-10" fill={GREEN_MID} data-id="element-1180" data-mp="2c4irgfa4dkjg">
          253
        </text>
      </svg>
      <div className="w-full flex items-center justify-center" style={{
      background: GREEN_MID,
      padding: `${size * 0.04}px 0`
    }} data-id="element-1181" data-mp="2l68qa66bzlr9">
        <span className="font-extrabold text-white" style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: size * 0.15,
        letterSpacing: size * 0.018,
        lineHeight: 1
      }} data-id="element-1182" data-mp="2h6hvlrdoolhg">
          GRAVEL
        </span>
      </div>
    </div>;
}
// SVG generators — produce standalone, downloadable files for each variant.
// Sized at 1200px wide for a crisp export; bake font-family / weight in so
// the file renders consistently outside the app.
// textLength + lengthAdjust forces the digits/wordmark into a fixed width
// regardless of which font the viewer falls back to, so "253" never clips
// at the edges when the SVG is opened outside the browser.
function variantAsvg() {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 1000" width="1200" height="1000">
  <rect width="1200" height="1000" fill="#ffffff"/>
  <text x="80" y="720" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="900" font-size="800" textLength="1040" lengthAdjust="spacingAndGlyphs" fill="${GREEN_DEEP}">253</text>
  <text x="120" y="920" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="800" font-size="190" textLength="960" lengthAdjust="spacingAndGlyphs" fill="${GREEN_DEEP}">GRAVEL</text>
</svg>`;
}
function variantBsvg() {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 1000" width="1200" height="1000">
  <rect width="1200" height="1000" fill="#ffffff"/>
  <text x="80" y="740" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="900" font-size="800" textLength="1040" lengthAdjust="spacingAndGlyphs" fill="${GREEN_MID}">253</text>
  <rect x="0" y="800" width="1200" height="180" fill="${GREEN_MID}"/>
  <text x="120" y="930" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="900" font-size="170" textLength="960" lengthAdjust="spacingAndGlyphs" fill="#ffffff">GRAVEL</text>
</svg>`;
}
function downloadSvg(filename: string, svg: string) {
  const blob = new Blob([svg], {
    type: 'image/svg+xml'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function LogoConcepts() {
  return <div className="min-h-screen w-full bg-white py-20 px-6 flex flex-col" data-id="element-1183" data-mp="1v3tldbvu4v8l">
      <div className="max-w-6xl mx-auto w-full flex-1" data-id="element-1184" data-mp="1r1q76fhs7cjm">
        <h1 className="font-heading text-4xl font-extrabold text-stone-900 mb-2" data-id="element-1185" data-mp="3pg9z0xqynbnh">
          Logo Concepts
        </h1>
        <p className="text-stone-600 mb-16" data-id="element-1186" data-mp="3lf7vw5txafg8">
          Two variations on white. Brand green replaces the tile background;
          “253” runs edge-to-edge with “GRAVEL” underneath.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1187" data-mp="1c4p2z0460ajo">
          {/* Variant A */}
          <div className="bg-white border border-stone-200 rounded-2xl p-10 flex flex-col items-center" data-id="element-1188" data-mp="1gvymdiz4ehn4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 mb-10" data-id="element-1189" data-mp="1kwqxkhrjywm1">
              Variant A — Typographic
            </p>
            <LogoVariantA size={360} data-id="element-1190" data-mp="o9m7waerpaur" />
            <div className="mt-12 flex items-end gap-8" data-id="element-1191" data-mp="t64elftnrj9q">
              <LogoVariantA size={200} data-id="element-1192" data-mp="t8qlrecmzwrf" />
              <LogoVariantA size={120} data-id="element-1193" data-mp="x7ya79ozxx31" />
              <LogoVariantA size={72} data-id="element-1194" data-mp="x6wl41b5ba2b" />
            </div>
          </div>

          {/* Variant B */}
          <div className="bg-white border border-stone-200 rounded-2xl p-10 flex flex-col items-center" data-id="element-1195" data-mp="128m0tu9bcbwq">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400 mb-10" data-id="element-1196" data-mp="28ph7ymjt57u7">
              Variant B — Green Bar
            </p>
            <LogoVariantB size={360} data-id="element-1197" data-mp="djotie6eilyd" />
            <div className="mt-12 flex items-end gap-8" data-id="element-1198" data-mp="dinbfpqywz75">
              <LogoVariantB size={200} data-id="element-1199" data-mp="9j62ung8vlxr" />
              <LogoVariantB size={120} data-id="element-1200" data-mp="9i3fgpi4s1ml" />
              <LogoVariantB size={72} data-id="element-1201" data-mp="ni5qpsvxnwsv" />
            </div>
          </div>
        </div>
      </div>

      {/* Temporary download footer — remove once user has the files. */}
      <footer className="mt-20 border-t border-stone-200 pt-8 pb-4" data-id="element-1202" data-mp="ih89biiqz6sf">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-1203" data-mp="3qzwclvjkef62">
          <div data-id="element-1204" data-mp="3m1urz66hq7sh">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400" data-id="element-1205" data-mp="3ly6z898vdpet">
              Downloads
            </p>
            <p className="text-sm text-stone-500 mt-1" data-id="element-1206" data-mp="3vxpguj1b4z3p">
              SVG, 1200×1000 px. Lossless and scalable to any size.
            </p>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-1207" data-mp="386sbjp40p01f">
            <button type="button" onClick={() => downloadSvg('253gravel-logo-variant-a.svg', variantAsvg())} className="px-4 py-2.5 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-sm font-bold transition-colors" data-id="element-1208" data-mp="33abqx4w113t3">
              Download Variant A (SVG)
            </button>
            <button type="button" onClick={() => downloadSvg('253gravel-logo-variant-b.svg', variantBsvg())} className="px-4 py-2.5 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-sm font-bold transition-colors" data-id="element-1209" data-mp="19r33csw6l48a">
              Download Variant B (SVG)
            </button>
          </div>
        </div>
      </footer>
    </div>;
}